import { useMemo } from 'react'

import { useActiveBookkeepingBankAccount } from '@features/accounts/hooks/useActiveBookkeepingBankAccount'
import { useBankTransactionsAccess } from '@features/bankTransactions/hooks/useBankTransactionsAccess'
import { useIsEInvoicingOnly } from '@features/bills/hooks/useIsEInvoicingOnly'
import { useImportedInvoicesFeature } from '@features/importedInvoices/hooks/useImportedInvoicesFeature'
import { useQuickStartProgress } from '@features/quickStart/hooks/useQuickStartProgress'
import { useIsFrenchUmbrellaOrganization } from '@features/transactions/hooks/useIsFrenchUmbrellaOrganization'
import { useUserOrganization, useUserOrganizationSettings } from '@modules-deprecated/app/organization'
import { useUmbrella } from '@modules-deprecated/app/umbrellas'
import { useCurrentUser } from '@modules-deprecated/app/user'
import { useIsAnnualReportAvailable } from '@views/annualReport/hooks/useIsAnnualReportAvailable'
import { useHasDeliveryNotes } from '@views/deliveryNotes/hooks/useHasDeliveryNotes'
import { useIsDmsEnabled } from '@views/documents/hooks/useIsDmsEnabled'
import { useReviewEntriesFeature } from '@views/reviewEntries/hooks/useReviewEntriesFeature'
import { useSalaryConnectionStatus } from '@views/salaryIntegration/hooks/useSalaryConnectionStatus'

import { LabsFlag } from '../../../../../enums/labsFlag'
import { Scope } from '../../../../../enums/scope'
import { useOrganizationSource } from '../../../../../hooks'
import { useLabsMenuFlags } from '../../../../../hooks/useLabsMenuFlags'
import { useOrgUsesReceiptsOnly } from '../../../../../hooks/useOrgUsesReceiptsOnly'
import { isAuthorized } from '../../../../../utils/isAuthorized'

export interface NavigationMenuVisibilityConditions {
  countryId?: string
  hasActiveBookkeepingBankAccount: boolean
  hasDeliveryNotes: boolean
  isAdmin: boolean
  isAnnualReportAvailable: boolean
  isBankTransactionsFlagEnabled: boolean
  orgUsesReceiptsOnly: boolean
  isEInvoicingOnly: boolean
  isFlagBankReactEnabled: boolean
  isImportedInvoicesFeatureEnabled: boolean
  isInboxEnabled: boolean
  isDmsEnabled: boolean
  isExpertReportsSuppliersBalanceEnabled: boolean
  isExpertReportsCustomersBalanceEnabled: boolean
  isExpertReportsTrialBalanceEnabled: boolean
  isExpertReportsProfitAndLossEnabled: boolean
  isExpertReportsBalanceSheetEnabled: boolean
  isExpertReportsVatDeclarationsEnabled: boolean
  isMyClientEnabled: boolean
  isQuickStartEligible: boolean
  isQuickStartSkipped: boolean
  isReconciliationViewVisible: boolean
  isExpertReportsEnabled: boolean
  hasLedgerEntriesAccess: boolean
  isReviewEntriesEnabled: boolean
  isSalaryAvailable: boolean
  isSourceBilly: boolean
  isSourceZervant: boolean
  isUmbrellaOwner: boolean
  isVatExempted: boolean
}

export const useNavigationMenuVisibilityConditions = () => {
  const { organization } = useUserOrganization()
  const isDmsEnabled = useIsDmsEnabled()
  const { isSalaryAvailable } = useSalaryConnectionStatus()
  const isAnnualReportAvailable = useIsAnnualReportAvailable()
  const { organizationSettings } = useUserOrganizationSettings()
  const { user } = useCurrentUser()
  const { umbrella } = useUmbrella()
  const labsFlags = useLabsMenuFlags()
  const { isSourceZervant, isSourceBilly } = useOrganizationSource()
  const hasDeliveryNotes = useHasDeliveryNotes()
  const { activeBookkeepingBankAccount } = useActiveBookkeepingBankAccount()
  const isEInvoicingOnly = useIsEInvoicingOnly()
  const orgUsesReceiptsOnly = useOrgUsesReceiptsOnly()
  const { isEnabled: isImportedInvoicesFeatureEnabled } = useImportedInvoicesFeature()
  const { isEligible: isQuickStartEligible, isSkipped: isQuickStartSkipped } = useQuickStartProgress()
  const { isEnabled: isReviewEntriesEnabled } = useReviewEntriesFeature()
  const { isTransactionsViewVisible: isBankTransactionsFlagEnabled, isReconciliationViewVisible } =
    useBankTransactionsAccess()
  const isFrenchUmbrellaOrganization = useIsFrenchUmbrellaOrganization()

  const { isAdmin = false } = user || {}

  const navigationMenuVisibilityConditions: NavigationMenuVisibilityConditions = useMemo(() => {
    const isFlagBankReactEnabled = labsFlags[LabsFlag.ReactBankReconciliation]
    const isInboxEnabled = umbrella?.isInboxEnabled ?? organizationSettings?.isInboxEnabled === '1'
    const isMyClientEnabled = labsFlags[LabsFlag.NavigationMyClient]
    const isUmbrellaOwner = !!umbrella && umbrella?.ownerUserId === user?.id
    const hasActiveBookkeepingBankAccount = !!activeBookkeepingBankAccount
    const isExpertReportsEnabled = labsFlags[LabsFlag.Reports]
    const isExpertReportsSuppliersBalanceEnabled = labsFlags[LabsFlag.ExpertReportsSuppliersBalance]
    const isExpertReportsCustomersBalanceEnabled = labsFlags[LabsFlag.ExpertReportsCustomersBalance]
    const isExpertReportsTrialBalanceEnabled = labsFlags[LabsFlag.ExpertReportsTrialBalance]
    const isExpertReportsProfitAndLossEnabled = labsFlags[LabsFlag.ExpertReportsProfitAndLoss]
    const isExpertReportsBalanceSheetEnabled = labsFlags[LabsFlag.ExpertReportsBalanceSheet]
    const isExpertReportsVatDeclarationsEnabled = labsFlags[LabsFlag.ExpertReportsVatDeclarations]
    const hasLedgerEntriesAccess = isFrenchUmbrellaOrganization || isAuthorized(Scope.TransactionRead)

    return {
      countryId: organization?.countryId,
      hasActiveBookkeepingBankAccount,
      hasDeliveryNotes,
      isAdmin,
      isAnnualReportAvailable,
      isBankTransactionsFlagEnabled,
      orgUsesReceiptsOnly,
      isEInvoicingOnly,
      isFlagBankReactEnabled,
      isImportedInvoicesFeatureEnabled,
      isDmsEnabled,
      isExpertReportsSuppliersBalanceEnabled,
      isExpertReportsCustomersBalanceEnabled,
      isExpertReportsTrialBalanceEnabled,
      isExpertReportsProfitAndLossEnabled,
      isExpertReportsBalanceSheetEnabled,
      isExpertReportsVatDeclarationsEnabled,
      isInboxEnabled,
      isMyClientEnabled,
      isQuickStartEligible,
      isQuickStartSkipped,
      isReconciliationViewVisible,
      isExpertReportsEnabled,
      hasLedgerEntriesAccess,
      isReviewEntriesEnabled,
      isSalaryAvailable,
      isSourceBilly,
      isSourceZervant,
      isUmbrellaOwner,
      isVatExempted: !!organization?.isVatExempted,
    }
  }, [
    activeBookkeepingBankAccount,
    hasDeliveryNotes,
    isAdmin,
    isAnnualReportAvailable,
    isBankTransactionsFlagEnabled,
    orgUsesReceiptsOnly,
    isEInvoicingOnly,
    isFrenchUmbrellaOrganization,
    isImportedInvoicesFeatureEnabled,
    isQuickStartEligible,
    isQuickStartSkipped,
    isReconciliationViewVisible,
    isReviewEntriesEnabled,
    isSalaryAvailable,
    isSourceBilly,
    isSourceZervant,
    labsFlags,
    isDmsEnabled,
    organization?.countryId,
    organization?.isVatExempted,
    organizationSettings?.isInboxEnabled,
    umbrella,
    user?.id,
  ])

  return { navigationMenuVisibilityConditions }
}
